import { BodyLink, BodyText, Stack } from '@kvdbil/components';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '~/Locale';
import {
  AccountVerificationStatus,
  useAccountVerificationStatus
} from '../../hooks/useAccountVerificationStatus';
import VerifyPhoneNumber from '~/App/shared/components/VerifyPhoneNumber/VerifyPhoneNumber';
import BiddingPrivCustomerBankIdVerification from './BiddingPrivCustomerBankIdVerification';
import AuthCloseButton from '~/App/shared/components/Auth/components/AuthCloseButton';
import BiddingPrivCustomerBankIdAndPhoneVerification from './BiddingPrivCustomerBankIdAndPhoneVerification';
import BiddingPrivMemberDataVerification from './BiddingPrivMemberDataVerification';
import BiddingOrgPersonBankIdAndPhoneVerification from './BiddingOrgPersonBankIdAndPhoneVerification';
import BiddingOrgPersonBankIdVerification from './BiddingOrgPersonBankIdVerification';
import BiddingOrgMemberDataVerification from './BiddingOrgMemberDataVerification';
import BiddingOrgPersonPhoneVerification from './BiddingOrgPersonPhoneVerification';

const Container = styled.div`
  text-align: center;
`;

const VerificationContainer = styled(Stack)`
  text-align: left;
`;

const LinkText = styled(BodyLink).attrs({ as: 'span' })`
  color: ${({ theme }) => theme.colors.info.dark};
  cursor: pointer;
`;

interface Props {
  setAuthInUse: (inUse: boolean) => void;
  isBiddingObject: boolean;
  accountVerificationStatus: AccountVerificationStatus;
  pendingStatuses: ReturnType<
    typeof useAccountVerificationStatus
  >['pendingStatuses'];
}

const BiddingUserAccountVerification = ({
  setAuthInUse,
  isBiddingObject,
  accountVerificationStatus,
  pendingStatuses
}: Props) => {
  const { t } = useTranslation();

  const [isAccountVerificationOpen, setIsAccountVerificationOpen] =
    useState(false);
  const [isBankIdVerificationPending, setIsBankIdVerificationPending] =
    useState(false);
  const [isPhoneVerificationPending, setIsPhoneVerificationPending] =
    useState(false);

  const {
    setIsBankIdAndPhoneVerificationPending,
    setIsOnlyBankIdVerificationPending,
    setIsOnlyPhoneVerificationPending
  } = pendingStatuses;

  const actionText = isBiddingObject
    ? t('to participate in the bidding.')
    : t('to buy the car at a fixed price');

  const handleClose = () => setIsAccountVerificationOpen(false);

  useEffect(() => {
    setAuthInUse(isAccountVerificationOpen);
  }, [setAuthInUse, isAccountVerificationOpen]);

  useEffect(() => {
    if (
      accountVerificationStatus === 'VERIFY_PRIV_CUSTOMER_BANKID_AND_PHONE' ||
      accountVerificationStatus === 'VERIFY_ORG_PERSON_BANKID_AND_PHONE'
    ) {
      setIsBankIdAndPhoneVerificationPending(
        isBankIdVerificationPending || isPhoneVerificationPending
      );
    }
    if (
      accountVerificationStatus === 'VERIFY_PRIV_CUSTOMER_BANKID' ||
      accountVerificationStatus === 'VERIFY_ORG_PERSON_BANKID'
    ) {
      setIsOnlyBankIdVerificationPending(isBankIdVerificationPending);
    }
    if (accountVerificationStatus === 'VERIFY_ORG_PERSON_PHONE') {
      setIsOnlyPhoneVerificationPending(isPhoneVerificationPending);
    }
  }, [
    accountVerificationStatus,
    setIsBankIdAndPhoneVerificationPending,
    setIsOnlyBankIdVerificationPending,
    setIsOnlyPhoneVerificationPending,
    isBankIdVerificationPending,
    isPhoneVerificationPending
  ]);

  useEffect(() => {
    return () => setAuthInUse(false);
  }, [setAuthInUse]);

  if (isAccountVerificationOpen) {
    switch (accountVerificationStatus) {
      case 'VERIFY_PRIV_CUSTOMER_BANKID_AND_PHONE':
      case 'VERIFY_ORG_PERSON_BANKID_AND_PHONE':
        const BankIdAndPhoneVerification =
          accountVerificationStatus === 'VERIFY_PRIV_CUSTOMER_BANKID_AND_PHONE'
            ? BiddingPrivCustomerBankIdAndPhoneVerification
            : BiddingOrgPersonBankIdAndPhoneVerification;
        return (
          <VerificationContainer>
            {!isBankIdVerificationPending && (
              <AuthCloseButton
                onClose={() => {
                  setIsPhoneVerificationPending(false);
                  handleClose();
                }}
              />
            )}
            <BankIdAndPhoneVerification
              isBankIdVerificationPending={isBankIdVerificationPending}
              setIsBankIdVerificationPending={setIsBankIdVerificationPending}
              isPhoneVerificationPending={isPhoneVerificationPending}
              setIsPhoneVerificationPending={setIsPhoneVerificationPending}
            />
          </VerificationContainer>
        );
      case 'VERIFY_PRIV_CUSTOMER_BANKID':
      case 'VERIFY_ORG_PERSON_BANKID':
        const BankIdVerification =
          accountVerificationStatus === 'VERIFY_PRIV_CUSTOMER_BANKID'
            ? BiddingPrivCustomerBankIdVerification
            : BiddingOrgPersonBankIdVerification;
        return (
          <VerificationContainer>
            {!isBankIdVerificationPending && (
              <AuthCloseButton onClose={handleClose} />
            )}
            <BankIdVerification
              isPending={isBankIdVerificationPending}
              setIsPending={setIsBankIdVerificationPending}
            />
          </VerificationContainer>
        );
      case 'VERIFY_PRIV_CUSTOMER_PHONE':
        return (
          <VerificationContainer>
            <AuthCloseButton onClose={handleClose} />
            <VerifyPhoneNumber isBiddingView handleClose={handleClose} />
          </VerificationContainer>
        );
      case 'VERIFY_ORG_PERSON_PHONE':
        return (
          <VerificationContainer>
            <AuthCloseButton onClose={handleClose} />
            <BiddingOrgPersonPhoneVerification
              isPending={isPhoneVerificationPending}
              setIsPending={setIsPhoneVerificationPending}
            />
          </VerificationContainer>
        );
      case 'VERIFY_PRIVATE_MEMBER_DATA':
      case 'VERIFY_ORG_MEMBER_DATA':
        const MemberDataVerification =
          accountVerificationStatus === 'VERIFY_PRIVATE_MEMBER_DATA'
            ? BiddingPrivMemberDataVerification
            : BiddingOrgMemberDataVerification;
        return (
          <VerificationContainer>
            <AuthCloseButton onClose={handleClose} />
            <MemberDataVerification handleClose={handleClose} />
          </VerificationContainer>
        );
      default:
        return null;
    }
  }

  return (
    <Container>
      <LinkText onClick={() => setIsAccountVerificationOpen(true)}>
        {t('Verify your details')}
      </LinkText>
      <BodyText>{actionText}</BodyText>
    </Container>
  );
};

export default BiddingUserAccountVerification;
